import { createHeadlessEditor } from '@lexical/headless';
import { $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import { PlaygroundNodes } from '@cogneed/design-system/wysiwyg-kit';
import { DecisionTreeRunResultFieldsFragment } from '../../../../../../../../../generated/core-api/graphql-react-query';
import { DecisionTreeReportQuery } from './useQueryAssistantMutation';

interface DecisionTreeReportQuestionInput {
  id: string;
  richText: string;
  value?: string;
  comment?: string;
}

interface DecisionTreeReportRunInput {
  name: string;
  id: string;
  llmQuestion: string;
  questions: DecisionTreeReportQuestionInput[];
  summary?: string;
}

async function convertRichTextToMarkdown(richText: string): Promise<string> {
  const editor = createHeadlessEditor({
    nodes: PlaygroundNodes,
    onError: () => {
      console.error('Error parsing editor state');
    },
  });
  editor.setEditorState(editor.parseEditorState(richText));

  return new Promise<string>((resolve) => {
    editor.update(() => {
      const markdown = $convertToMarkdownString(TRANSFORMERS);
      resolve(markdown);
    });
  });
}

export default function useDecisionTreeReportQueries(decisionTreeRunId: string, result?: DecisionTreeRunResultFieldsFragment | null): UseQueryResult<DecisionTreeReportQuery[], unknown> {
  const treeRuns = React.useMemo<DecisionTreeReportRunInput[]>(() => {
    if (!result) {
      return [];
    }

    return [
      {
        name: result.decisionTree.name,
        id: decisionTreeRunId,
        llmQuestion: result.decisionTree.llmQuestion,
        summary: result.summary,
        questions: result.history.map((item) => {
          const isSingleChoiceWithComment = item.node.children.length === 1 && item.comment;
          return {
            id: item.nodeId,
            richText: item.node.richText,
            ...(isSingleChoiceWithComment ? {
              value: item.comment ?? '',
            } : {
              value: item.value,
              comment: item.comment,
            }),
          };
        }),
      },
      ...(result.sequence?.map((seq) => ({
        name: seq.run.decisionTree.name,
        id: seq.runId,
        llmQuestion: seq.run.decisionTree.llmQuestion,
        summary: seq.run.summary,
        questions: (seq.run?.history ?? []).map((item) => {
          const isSingleChoiceWithComment = item.node.children.length === 1 && item.comment;
          return {
            id: item.nodeId,
            richText: item.node.richText,
            ...(isSingleChoiceWithComment ? {
              value: item.comment ?? '',
            } : {
              value: item.value,
              comment: item.comment,
            }),
          };
        }),
      })) ?? []),
    ].filter((treeRun) => !!treeRun.llmQuestion);
  }, [decisionTreeRunId, result]);

  const queryKey = React.useMemo(
    () => treeRuns.map((treeRun) => ({ id: treeRun.id, history: treeRun.questions.map((question) => question.value) })),
    [treeRuns],
  );

  const query = useQuery(
    ['getHistoryWithPrompts', queryKey],
    async () => {
      const treePromises = treeRuns.map(async (treeRun) => {
        const questions = await Promise.all(
          treeRun.questions.map(async (question) => {
            const markdown = await convertRichTextToMarkdown(question.richText);
            return {
              id: question.id, markdown, value: question.value, comment: question.comment,
            };
          }),
        );

        return {
          name: treeRun.name,
          id: treeRun.id,
          questions,
          llmQuestion: treeRun.llmQuestion,
          summary: treeRun.summary,
        };
      });
      const results = await Promise.all(treePromises);
      return results as DecisionTreeReportQuery[];
    },
    {
      enabled: !!result && treeRuns.length > 0,
    },
  );
  return query;
}
